import { useState, useEffect } from 'react';
import { CheckIcon, CloseIcon } from './Icons.jsx';
import './NumericQuestion.css';

const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '-', '0', '.'];

const NUMERIC_RE = /^[-+]?(\d+\.?\d*|\.\d+)$/;

/**
 * Mirrors NumericQuestion.checkCorrectness on the server (models/Question.js):
 * the value has to match AND so does whether it is written with a decimal
 * point, but trailing zeros are just formatting. Kept in step with the server
 * so the phone never contradicts the points it was just awarded.
 */
function sameAnswer(given, expected) {
  const a = String(given ?? '').trim().replace(/,/g, '');
  const b = String(expected ?? '').trim().replace(/,/g, '');
  if (!NUMERIC_RE.test(a) || !NUMERIC_RE.test(b)) return false;
  return Number(a) === Number(b) && a.includes('.') === b.includes('.');
}

/**
 * Type the value an expression evaluates to. The digits come from a keypad
 * drawn in the page rather than the phone's own keyboard: a Thai keyboard
 * opens on the Thai layer and hides half the screen, and here the decimal
 * point is the whole point of the question — `15` and `15.0` are different
 * answers, so the dot has to be impossible to miss.
 */
export default function NumericQuestion({ question, answered, onSubmit }) {
  const { payload, revealed } = question;
  const isLocked = answered || Boolean(revealed);
  const [text, setText] = useState('');

  useEffect(() => {
    setText('');
  }, [question.id]);

  const press = (key) => {
    if (isLocked) return;
    setText((prev) => {
      if (key === '.' && prev.includes('.')) return prev;
      if (key === '-') return prev.startsWith('-') ? prev.slice(1) : `-${prev}`;
      if (prev.replace('-', '').replace('.', '').length >= 12) return prev;
      return prev + key;
    });
  };

  const backspace = () => {
    if (isLocked) return;
    setText((prev) => prev.slice(0, -1));
  };

  const submit = () => {
    if (isLocked || !text) return;
    onSubmit({ text });
  };

  // A phone that reloaded mid-question has no local answer to judge; the
  // server knows what was submitted but never sends it back, so say nothing
  // about right or wrong rather than calling a correct answer wrong.
  const knowsAnswer = text !== '';
  const isRight = revealed && knowsAnswer && sameAnswer(text, payload.answer);
  const isWrong = revealed && knowsAnswer && !isRight;

  return (
    <div className="card stack">
      <div className="row-between">
        <span className="q-kicker">ตอบเป็นตัวเลข</span>
      </div>
      <h2>{payload.question}</h2>

      <div className="numeric-expression num">
        <span>{payload.expression}</span>
        <span className="numeric-eq">=</span>
        <span className={`numeric-slot ${text ? 'is-filled' : ''} ${isRight ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`}>
          {text || '?'}
        </span>
      </div>

      {payload.hint && !revealed && <p className="numeric-hint">{payload.hint}</p>}

      {!isLocked && (
        <>
          <div className="numeric-keypad">
            {KEYS.map((key) => (
              <button key={key} type="button" className="numeric-key num" onClick={() => press(key)}>
                {key}
              </button>
            ))}
            <button type="button" className="numeric-key is-back" onClick={backspace}>
              ลบ
            </button>
          </div>
          <button className="btn btn-primary btn-block" disabled={!text} onClick={submit}>
            ยืนยันคำตอบ
          </button>
        </>
      )}

      {isLocked && !revealed && (
        <p className="text-dim text-center">
          {answered ? 'ส่งคำตอบแล้ว รอครูเฉลย...' : 'เฉลยคำตอบแล้ว — ปิดรับคำตอบข้อนี้'}
        </p>
      )}

      {revealed && (
        <div className={`numeric-reveal ${isRight ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`}>
          <span className="numeric-reveal-mark" aria-hidden="true">
            {isWrong ? <CloseIcon size={18} /> : <CheckIcon size={18} />}
          </span>
          <span>
            คำตอบที่ถูกต้องคือ <strong className="num">{payload.answer}</strong>
          </span>
        </div>
      )}

      {revealed && payload.revealNote && <p className="numeric-note">{payload.revealNote}</p>}
    </div>
  );
}
